import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from '../../reduxHooks';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '@betareader/design-system';
import Icon from '../../components/AppIcon';

import { CenteredContent } from 'src/style';
import { featureToggleUtil } from '../../utils';
import CreditBalanceChip from 'src/components/credits/CreditBalanceChip';
import OutOfCreditsModal from 'src/components/credits/OutOfCreditsModal';
import { useCredits, CreditHistoryItem } from 'src/hooks/useCredits';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';

const MyCenteredContent = styled(CenteredContent)`
  padding-left: 1rem;
  padding-right: 1rem;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  flex: 1;
  font-size: 1.5em;
  font-weight: bold;
  margin: 0;
`;

const Section = styled(Card)`
  margin-top: 16px;
  padding: 20px;
`;

const HistoryList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const HistoryRow = styled.li`
  align-items: center;
  border-top: 1px solid var(--br-border);
  display: flex;
  gap: 12px;
  padding: 12px 0;
  &:first-child {
    border-top: 0;
  }
`;

export const iconForType = (type: string) => {
  switch (type) {
    case 'grant':
    case 'grant_monthly':
    case 'monthly_grant':
      return 'gift';
    case 'purchase':
    case 'pack':
      return 'shopping cart';
    case 'consume':
    case 'usage':
      return 'lightning';
    case 'refund':
      return 'undo';
    default:
      return 'circle';
  }
};

type HistoryItemForLabel = Pick<CreditHistoryItem, 'type' | 'feature'>;
type Translate = (key: string, fallback: string) => string;

const historyFeatureLabel = (feature: string | undefined, t: Translate) => {
  switch (feature) {
    case 'audiobook':
      return t('CreditHistoryAudiobook', 'Audiobook');
    case 'audiobook_voice_sample':
      return t('CreditHistoryAudiobookVoiceSample', 'Audiobook voice sample');
    case 'feedback_digest':
      return t('CreditHistoryFeedbackDigest', 'Feedback digest');
    case 'editor_feedback':
      return t('CreditHistoryEditorFeedback', 'Editorial feedback');
    case 'editor_feedback_rerun':
      return t('CreditHistoryEditorFeedbackRerun', 'Editorial feedback rerun');
    case 'classifier':
      return t('CreditHistoryClassifier', 'Manuscript classification');
    case 'summarize':
      return t('CreditHistorySummarize', 'Manuscript summary');
    case 'publishability':
      return t('CreditHistoryPublishability', 'Publishability analysis');
    case 'tts':
      return t('CreditHistoryTts', 'Text to speech');
    case 'submit_inbox':
      return t('CreditHistorySubmitInbox', 'Manuscript submission');
    default:
      return undefined;
  }
};

export const historyItemLabel = (item: HistoryItemForLabel, t: Translate) => {
  switch (item.type) {
    case 'grant_monthly':
    case 'monthly_grant':
      return t('MonthlyCreditGrant', 'Monthly credit grant');
    case 'grant':
    case 'grant_manual':
      return t('CreditHistoryGrant', 'Credit grant');
    case 'purchase':
    case 'pack':
      return t('CreditHistoryPurchase', 'Credit purchase');
    case 'revoke_purchase':
      return t('CreditHistoryRevokePurchase', 'Credit purchase revoked');
    case 'restore_purchase':
      return t('CreditHistoryRestorePurchase', 'Credit purchase restored');
    case 'expire':
      return t('CreditHistoryExpire', 'Credits expired');
    case 'refund':
      return [
        t('CreditHistoryRefund', 'Credit refund'),
        historyFeatureLabel(item.feature, t),
      ]
        .filter(Boolean)
        .join(' · ');
    case 'consume':
    case 'usage':
      return (
        historyFeatureLabel(item.feature, t) ||
        t('CreditHistoryUsage', 'Credit usage')
      );
    default:
      return t('CreditHistoryTransaction', 'Credit transaction');
  }
};

// `embedded` renders this as a subsection (e.g. inside /account/subscription):
// it drops the page <Helmet> title and, when the AI editor is unavailable,
// renders nothing instead of a standalone "not available" panel.
const BookCredits: React.FC<{ embedded?: boolean }> = ({
  embedded = false,
}) => {
  const { t } = useTranslation();
  const currentBook = useSelector((state: any) => state.book.currentBook);
  const userProfile = useSelector((state: any) => state.user.userProfile);
  const { data: credits } = useCredits();
  const { flags, isLoading: flagsLoading } = useFeatureFlags();
  const aiEditorEnabled =
    flags?.ai_editor === true ||
    featureToggleUtil.hasFeatureAccess(userProfile, 'ai_editor');
  const [modalOpen, setModalOpen] = useState(false);

  const balance = credits?.balance ?? 0;
  const period = credits?.period;
  const history = credits?.history || [];

  if (!flagsLoading && !aiEditorEnabled) {
    if (embedded) return null;
    return (
      <MyCenteredContent>
        <Section>
          {t('FeatureNotAvailable', 'This feature is not available.')}
        </Section>
      </MyCenteredContent>
    );
  }

  return (
    <>
      {!embedded && (
        <Helmet>
          <meta charSet="utf-8" />
          <title>{t('UsageAndCredits', 'Usage & credits')}</title>
        </Helmet>
      )}
      <MyCenteredContent>
        <HeaderRow>
          <Title>{t('UsageAndCredits', 'Usage & credits')}</Title>
          {currentBook && (
            <CreditBalanceChip usageHref="/account/subscription" />
          )}
        </HeaderRow>

        {/* Current balance */}
        <Section>
          <div style={{ fontSize: 24, fontWeight: 800 }}>
            <Icon name="lightning" aria-hidden="true" /> {balance}
          </div>
          <div>{t('CreditsBalance', 'Credits')}</div>
          {credits?.resetDate && (
            <p style={{ color: 'var(--br-muted)' }}>
              {credits?.productInternalId === 'free'
                ? t('FreePlan', 'Free plan')
                : t('PremiumPlan', 'Premium plan')}{' '}
              ·{' '}
              {t('ResetsOn', 'resets {{date}}', {
                date: new Date(credits.resetDate).toLocaleDateString(),
              })}{' '}
              · {credits?.monthlyCredits ?? 0}/{t('Month', 'mo')}
            </p>
          )}
          <Button onClick={() => setModalOpen(true)}>
            {t('BuyMoreCredits', 'Buy more credits')}
          </Button>
        </Section>

        {/* Period stats */}
        {period && (
          <Section>
            <strong>{balance}</strong> {t('Remaining', 'Remaining')}
          </Section>
        )}

        {/* History */}
        <Section>
          <h4>{t('History', 'History')}</h4>
          <HistoryList>
            {history.map((item: CreditHistoryItem) => (
              <HistoryRow key={item.id}>
                <Icon
                  name={iconForType(item.type)}
                  size="large"
                  aria-hidden="true"
                />
                <span style={{ flex: 1 }}>
                  <strong>{historyItemLabel(item, t)}</strong>
                  <span style={{ display: 'block', color: 'var(--br-muted)' }}>
                    {new Date(item.date).toLocaleDateString()}
                    {item.meta?.reason ? ` · ${item.meta.reason}` : ''}
                  </span>
                </span>
                <span
                  style={{
                    color:
                      item.amount >= 0
                        ? 'var(--br-success-text)'
                        : 'var(--br-error-text)',
                  }}
                >
                  {item.amount >= 0 ? '+' : '−'}
                  {Math.abs(item.amount)}
                </span>
              </HistoryRow>
            ))}
          </HistoryList>
        </Section>
      </MyCenteredContent>
      <OutOfCreditsModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};

export default BookCredits;
